import {
  Box,
  CloseIcon,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
} from "@hightouch/htui";
import { wrapComponentForStandaloneRender } from "@hightouch/core/content/component-normalization/canonical-style";

import { EmailPreview, LazyEmailPreview } from "src/components/email-preview";
import { useComponentEmailStyleConfig } from "src/pages/lifecycle-studio/emails/components/use-component-email-style-config";
import type { SelectedComponent } from "./types";

// Small square shown inline in the composer, matching the uploaded-image
// thumbnail footprint.
const THUMBNAIL_SIZE = 64;
// Larger square preview shown on hover — same treatment as the template picker's
// EmailOptionWithPreview popover.
const PREVIEW_SIZE = 500;

type Props = {
  component: SelectedComponent;
  onRemove: () => void;
  /** Hide the remove button — used when rendering sent-message history. */
  readOnly?: boolean;
};

/**
 * A picked component in the composer: a small square preview with the component
 * name on hover, plus a larger rendered popover preview (like the template
 * picker's option preview) and a remove button. In `readOnly` mode (sent-message
 * history) the remove button is hidden.
 */
export const ComponentThumbnail = ({
  component,
  onRemove,
  readOnly = false,
}: Props) => {
  const emailStyleConfig = useComponentEmailStyleConfig();

  // Component HTML is a headless fragment; wrap it in the canonical responsive
  // block + content-width body rule so it renders in its centered column
  // instead of stretching full-width.
  const html = component.html
    ? wrapComponentForStandaloneRender(component.html, emailStyleConfig)
    : null;

  const thumbnail = (
    <Box
      position="relative"
      flexShrink={0}
      width={`${THUMBNAIL_SIZE}px`}
      height={`${THUMBNAIL_SIZE}px`}
      borderRadius="md"
      overflow="hidden"
      border="1px solid"
      borderColor="base.border"
    >
      <LazyEmailPreview
        html={html}
        width={THUMBNAIL_SIZE}
        height={THUMBNAIL_SIZE}
        scrollable
      />
      {!readOnly && (
        <Box position="absolute" top={0.5} right={0.5}>
          <IconButton
            aria-label={`Remove ${component.name}`}
            icon={CloseIcon}
            size="sm"
            variant="secondary"
            shape="circle"
            onClick={onRemove}
          />
        </Box>
      )}
    </Box>
  );

  if (!html) {
    return thumbnail;
  }

  return (
    <Popover
      trigger="hover"
      placement="top"
      // Position against the viewport (not the composer's clipping box) so the
      // preview has room to open above the input.
      strategy="fixed"
      isLazy
      gutter={12}
    >
      <PopoverTrigger>{thumbnail}</PopoverTrigger>
      <Portal>
        <PopoverContent
          p={0}
          overflow="hidden"
          w={`${PREVIEW_SIZE}px`}
          maxW={`${PREVIEW_SIZE}px`}
          zIndex="tooltip"
        >
          <Box px={3} py={2} borderBottom="1px solid" borderColor="base.border">
            <Text size="sm" fontWeight="medium" isTruncated>
              {component.name}
            </Text>
          </Box>
          <EmailPreview
            html={html}
            width={PREVIEW_SIZE}
            height={PREVIEW_SIZE}
            scrollable
            resetScrollOnMouseLeave={false}
          />
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
